'use strict';

import React from "react";
import {Breakpoints} from "./AppConstants";
import {isEmpty} from "@uw-it-sis/lib-js-common/lib/Utils";
import TermUtils from "./TermUtils";
import moment from 'moment-timezone';
// Re-export all utils from lib-js-common, so that they can be accessed from here.
export * from '@uw-it-sis/lib-js-common/lib/Utils';
export * from '@uw-it-sis/lib-js-common/lib/guid';

// FIXME: to prevent breakage until all usages are found and fixed (some utils that were located here moved to CourseUtils)
export * from "./CourseUtils";

/**
 * Reads the value of the given property from the given object.
 * Returns undefined if an undefined property is encountered.
 *
 * @param propertyPath The path to the property (e.g. foo.bar)
 * @param object The object to read the property value from.
 * @returns The value the given property if it is set. Otherwise, undefined.
 */
export function v(propertyPath, object) {
    let value = "";
    let path = propertyPath.split('.');
    try {
        value = path.reduce(function(obj, prop) {
                                return obj[prop];
                            }, object);
    } catch(e) {
        //  Swallow the exception.
    }
    return value;
}

/*
 * Unix logging-style timestamp: 2016-01-27 16:41:17,866
 */
export function unixTimestamp() {
    return moment().format("YYYY-MM-DD HH:mm:ss,SSS");
}

/**
 * Attempts to determine the browser agent name and version(s).
 *
 * This code came off of the internets, so there may be some room for improvement.
 *
 * @returns {name: "" version: ""}
 */
export function getBrowserInfo() {
    if (! navigator) {
        return {name: "Unknown", version: "Unknown"};
    }

    let ua = navigator.userAgent, tmp;

    let m = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];

    // IE
    if (/trident/i.test(m[1])) {
        tmp = /\brv[ :]+(\d+)/g.exec(ua) || [];
        return {name:'IE',version:(tmp[1]||'')};
    }

    if (m[1] === 'Chrome') {
        tmp = ua.match(/\bOPR\/(\d+)/);
        if (tmp != null) {
            return {name:'Opera', version:tmp[1]};
        }
    }

    m = m[2] ? [m[1], m[2]]: [navigator.appName, navigator.appVersion, '-?'];

    if ((tmp=ua.match(/version\/(\d+)/i)) != null) {
        m.splice(1,1,tmp[1]);
    }

    return {name: m[0], version: m[1]};
 }

/**
 * Evaluates an object to see if all properties equal false, if so return true.
 */
export function allFalse(obj) {
    for (let i in obj) {
        if (obj[i] === true) return false;
    }
    return true;
}

/**
 * Evaluates an object to see if all properties equal true, if so return true.
 */
export function allTrue(obj) {
    for (let i in obj) {
        if (obj[i] === false) return false;
    }
    return true;
}

/**
 * intersperse: Return an array with the separator interspersed between
 * each element of the input array.
 *
 * > _([1,2,3]).intersperse(0)
 * [1,0,2,0,3]
 */
export function intersperse(arr, sep) {
    if (arr.length === 0) {
        return [];
    }

    return arr.slice(1).reduce(function(xs, x, i) {
        return xs.concat([sep, x]);
    }, [arr[0]]);
}

/**
 * groupBy: Group an array of objects by a common key value
 */
export function groupBy(arr, prop) {
    return arr.reduce(function(groups, item) {
        const val = item[prop];
        groups[val] = groups[val] || [];
        groups[val].push(item);
        return groups;
    }, {})
}

/**
 * capitalize: Capitalize the first character of a string.
 * @param s: The string to be capitalized
 * @return {string}
 */
export function capitalize(s) {
    if (typeof s !== 'string') return '';
    return s.charAt(0).toUpperCase() + s.toLowerCase().slice(1);
}

/**
 * makeId: Cleanup a string into a string that can be used in an HTML id attribute
 * @param s: The string to converted
 * @return {string}
 */
export function makeId(s) {
    return s.replace(/\s+|&/g, '').replace(/:/g, '-').toLowerCase();
}

/**
 * makeUniqueId: Create a unique 9 character string
 * @return {string}
 */
export function makeUniqueId() {
    return Math.random().toString(36).substr(2, 9);
}


/**
 * pluralize: Create a string that is either singular or plural
 * @param text: string/word to return singular or plural
 * @param count: size of the items
 * @return {string}
 */
export function pluralize(text, count) {
    return text + (parseInt(count) === 1 ? "" : "s");
}

/**
 * Returns the innerHtml if there is a red banner on the page the user is on else an empty String.
 * @param text: document (DOM)
 * @return {string}
 */
export function getErrorsOnPage(document) {
    let errorsOnPage = "";

    if (document && document.getElementsByClassName("alert-danger")
        && document.getElementsByClassName("alert-danger")[0]){
        if (document.getElementsByClassName("alert-danger")[0].getElementsByTagName("UL")
            && document.getElementsByClassName("alert-danger")[0].getElementsByTagName("UL")[0]){
            errorsOnPage = document.getElementsByClassName("alert-danger")[0].getElementsByTagName("UL")[0].innerHTML;
        }
    }

    return errorsOnPage;
}

/**
 * getBreakpointSize: Returns the current bootstrap breakpoint size
 */
export function getBreakpointSize() {
    let width = window.innerWidth;
    let breakpoint;

    switch (true) {
        // xs
        case (width >= Breakpoints.xs.width && width < Breakpoints.sm.width):
            breakpoint = Breakpoints.xs.code;
            break;
        // sm
        case (width >= Breakpoints.sm.width && width < Breakpoints.md.width):
            breakpoint = Breakpoints.sm.code;
            break;
        // md
        case (width >= Breakpoints.md.width && width < Breakpoints.lg.width):
            breakpoint = Breakpoints.md.code;
            break;
        // lg
        case (width >= Breakpoints.lg.width && width < Breakpoints.xl.width):
            breakpoint = Breakpoints.lg.code;
            break;
        // xl
        case (width >= Breakpoints.xl.width):
            breakpoint = Breakpoints.xl.code;
            break;
    }

    return breakpoint;
}

/**
 * Returns the url string with any query params removed.
 * e.g. given
 *     https://www.google.com/search?hl=en&q=javascript
 * would return
 *     https://www.google.com/search
 */
export function stripQueryParams(url) {
    let queryTokenIndex = url.indexOf('?');
    if (queryTokenIndex !== -1) {
        url = url.slice(0, queryTokenIndex);
    }
    return url;
}

/**
 * Test to see if "now" is after, before, or between dates from within the academic calendar using the moment library
 * @param type - Options are "after", "before", "between"
 * @param shift - object of time shift; key being the period to shift and the value being the number, only works for before or after. For keys: https://momentjs.com/docs/#/manipulating/add/
 * @param academicCalendarKey - prop of the academic calendar date entry to check against.
 * "between" can either be between two academic calendar keys (passed as list of the two keys)
 * or a single key if it has a start and end date specified.
 * @returns {*|boolean}
 */
export function isNow (type, academicCalendarKey, appStore, termData, shift = {}){
    let result = false;
    // check to see if term data is present and prop to look for is specified
    if (academicCalendarKey && !isEmpty(termData) && termData.academicCalendar && termData.academicCalendar.length > 0 ) {
        let academicCalendarItem;
        if (type === "between" && typeof academicCalendarKey === 'object') {
            // if a list of keys passed, find those keys in academic calendar
            academicCalendarItem = termData.academicCalendar.filter(i => academicCalendarKey.includes(i.key));
            // warn if the filter does not return exactly 2 calendar dates from the academic calendar
            if (appStore.isDebug() && academicCalendarItem.length !== 2) console.warn(`[${this.storeName}] isNow: 'between' keys did not find exactly 2 academic calendar dates`);
        } else {
            academicCalendarItem = termData.academicCalendar.find(i => i.key === academicCalendarKey);
        }

        let shiftNum = 0, shiftPeriod = "d"; // d is shorthand for days; Keys can be found at: https://momentjs.com/docs/#/manipulating/add/
        if (!isEmpty(shift)) {
            shiftPeriod = Object.keys(shift)[0];
            shiftNum = Object.values(shift)[0];
        }

        if (academicCalendarItem) {
            // get now time from appStore (changeable via now widget) at set to Seattle time as a system time
            const now = moment.tz( new Date( appStore.getNow() ), SeattleTimeZone);
            let test;
            switch (type) {
                case "before":
                    // set the test to the academic calendar date 12:00:00am Seattle time (beginning of day)
                    test = moment.tz(academicCalendarItem.date, SeattleTimeZone).startOf("day").add(shiftNum, shiftPeriod) ?? ""
                    result =  now.isBefore(test);
                    break;
                case "after":
                    // set the test to the academic calendar date 11:59:59pm Seattle time (end of day)
                    test = moment.tz(academicCalendarItem.date, SeattleTimeZone).endOf("day").add(shiftNum, shiftPeriod) ?? ""
                    result = now.isAfter(test);
                    break;
                case "between":
                    if (typeof academicCalendarKey === 'object') {
                        // build an array of dates
                        test = academicCalendarItem.map((d, index) => {
                            // create moment object from academic calendar date
                            let academicCalendarDateMomentObj = moment.tz(d.date, SeattleTimeZone);
                            // if it's the first index, use start of day; if it's the last, use end of day
                            return index === 0 ? academicCalendarDateMomentObj.startOf("day") : academicCalendarDateMomentObj.endOf("day");
                        })
                    } else {
                        test = [
                            // the start date
                            moment.tz(academicCalendarItem.date, SeattleTimeZone).startOf("day") ?? "",
                            // the end date
                            moment.tz(academicCalendarItem.endDate, SeattleTimeZone).endOf("day") ?? ""
                        ];
                    }
                    result = now.isBetween(test[0], test[1])
                    break;
            }
        } else {
            if (appStore.isDebug()) console.warn(`isNow: did not find academic calendar date(s)`);
        }

        if (appStore.isDevMode()) {
            console.log(`[Utils.isNow] For term [ ${TermUtils.makeTermDescriptionFromTermId(termData.id)} ]: is 'now' [ ${type} ] [ ${academicCalendarKey} ]${shiftNum === 0 ? "" : "[ shifted by " + JSON.stringify(shift) + " ]"} ? ${result}`);
        }
    }

    return result;
}
export const SeattleTimeZone = "America/Los_Angeles"